const unauthorizedMessage = 'Unauthorized';
const defaultHttpClientOptions = {
  refreshCookieUrl: null,
  onInvalidToken: null,
  rawResponse: false
};

const defaultFetchOptions = {
  credentials: 'include',
  cache: 'no-cache'
};

const statusCodesWithExpectedTextResult = [201, 204];

class HttpClient {
  constructor(options) {
    this.options = {...defaultHttpClientOptions, ...options};
    this.pendingRefreshCookieRequest = null;
  }

  async refreshCookie() {
    try {
      let res = await fetch(this.options.refreshCookieUrl, defaultFetchOptions);
      if (res.status !== 200) return Promise.reject(`${unauthorizedMessage} - status code ${res.status}`);
      return res;
    } catch {
      return Promise.reject(unauthorizedMessage);
    }
  }

  getRefreshCookieRequest() {
    if (!this.pendingRefreshCookieRequest) {
      this.pendingRefreshCookieRequest = this.refreshCookie().finally(() => {
        this.pendingRefreshCookieRequest = null;
      });
    }

    return this.pendingRefreshCookieRequest;
  }

  isRefreshCookieSupported() {
    return this.options.refreshCookieUrl != null && this.options.refreshCookieUrl.length >= 8;
  }

  async request(doFetch, options) {
    let res = await doFetch();
    if (this.isRefreshCookieSupported() && (res.status === 401 || res.status === 403)) {
      try {
        await this.getRefreshCookieRequest();
      } catch (error) {
        if (options.onInvalidToken) {
          const result = options.onInvalidToken(error);
          if (result !== undefined) return result;
        }
        return Promise.reject(error);
      }

      res = await doFetch();
    }

    if (res.status < 200 || res.status > 299) {
      const text = await res.text();
      throw new Error(`
      Error fetching data:\n
      Status: ${res.status} ${res.statusText},
      URL: ${res.url},
      Text: ${text}
    `);
    }

    if (options.rawResponse) {
      return res;
    }

    if (statusCodesWithExpectedTextResult.includes(res.status)) {
      return res.text();
    }

    return res.json();
  }

  get(url, options) {
    return this.request(() => fetch(url, defaultFetchOptions), {...this.options, ...options});
  }

  post(url, data, options) {
    return this.request(() => fetch(url, {
      ...defaultFetchOptions,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }), {...this.options, ...options});
  }

  postForm(url, formData, options) {
    return this.request(() => fetch(url, {
      ...defaultFetchOptions,
      method: 'POST',
      body: formData
    }), {...this.options, ...options});
  }

  put(url, data, options) {
    return this.request(() => fetch(url, {
          ...defaultFetchOptions,
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        }),
        {...this.options, ...options});
  }

  del(url, options) {
    return this.request(() => fetch(url, {...defaultFetchOptions, method: 'DELETE'}), {...this.options, ...options});
  }

}

export default HttpClient
